import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  AudioQueue,
  type AudioCapture,
  startCapture,
} from "../lib/audio.ts";
import {
  openSocket,
  type ServerMessage,
} from "../lib/ws.ts";
import { Maximize, Minimize } from "lucide-react";
import { Card } from "../components/ui/card.tsx";
import { MicButton } from "../components/MicButton.tsx";
import {
  type MicMode,
  MicModeSelector,
} from "../components/MicModeSelector.tsx";
import { LanguageSelector } from "../components/LanguageSelector.tsx";
import { SpokenLanguageSelector } from "../components/SpokenLanguageSelector.tsx";
import {
  type Activity,
  ActivitySelector,
} from "../components/ActivitySelector.tsx";
import {
  DEFAULT_SETTINGS,
  SettingsPanel,
  type SettingsValue,
} from "../components/SettingsPanel.tsx";
import {
  type Transcript,
  TranscriptBubble,
} from "../components/TranscriptBubble.tsx";
import {
  StatusBanner,
  type Status,
} from "../components/StatusBanner.tsx";

export function ChatPage() {
  const [status, setStatus] = useState<Status>("offline");
  const [message, setMessage] = useState<string>();
  const [language, setLanguage] = useState("en");
  const [spokenLanguage, setSpokenLanguage] = useState("en");
  const [activity, setActivity] =
    useState<Activity>("mirror");
  const [settings, setSettings] =
    useState<SettingsValue>(DEFAULT_SETTINGS);
  const [transcripts, setTranscripts] = useState<
    Transcript[]
  >([]);
  const [self, setSelf] = useState<string>("");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [listening, setListening] = useState(false);
  const [connected, setConnected] = useState(false);
  const [micMode, setMicMode] = useState<MicMode>("handsFree");

  const wsRef = useRef<WebSocket | null>(null);
  const captureRef = useRef<AudioCapture | null>(null);
  const startPendingRef = useRef(false);
  const queueRef = useRef<AudioQueue | null>(null);
  const idRef = useRef(0);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // Persistent audio queue (must be a singleton)
  if (!queueRef.current)
    queueRef.current = new AudioQueue();

  const handleMessage = useCallback((m: ServerMessage) => {
    if (m.type === "hello") {
      setSelf(m.message);
    } else if (m.type === "audio") {
      setTranscripts((prev) => [
        ...prev,
        {
          id: ++idRef.current,
          actor: m.actor,
          input: m.input,
          text: m.text,
          mine: m.actor === selfRef.current,
          data: m.data,
          kind: m.kind,
        },
      ]);
      queueRef.current?.enqueue(m.data);
      setStatus("speaking");
      setMessage(undefined);
      // Drop back to listening shortly after, and close the WS if the user
      // asked for it (hands-free stop).
      window.setTimeout(() => {
        setStatus((s) =>
          s === "speaking"
            ? listeningRef.current
              ? "listening"
              : "idle"
            : s,
        );
      }, 1500);
    } else if (m.type === "transcript") {
      // Non-audio conversation entry (currently used for ZeroClaw agent errors).
      setTranscripts((prev) => [
        ...prev,
        {
          id: ++idRef.current,
          actor: m.actor,
          input: m.input ?? "",
          text: m.text,
          mine: false,
          kind: m.kind,
        },
      ]);
    } else if (m.type === "info") {
      if (m.message) {
        setStatus("thinking");
        setMessage(m.message);
      } else {
        setMessage(undefined);
        setStatus(
          listeningRef.current ? "listening" : "idle",
        );
      }
    } else if (m.type === "error") {
      setStatus("error");
      setMessage(m.message);
    } else if (m.type === "close") {
      setStatus("idle");
      setMessage(m.message);
    }
  }, []);

  // refs that mirror state for use inside callbacks
  const selfRef = useRef("");
  const listeningRef = useRef(false);
  const disconnectRef = useRef<() => void>(() => {});
  useEffect(() => {
    selfRef.current = self;
  }, [self]);
  useEffect(() => {
    listeningRef.current = listening;
  }, [listening]);

  const connect = () => {
    if (
      wsRef.current &&
      wsRef.current.readyState !== WebSocket.CLOSED
    ) {
      return;
    }
    const ws = openSocket(handleMessage, (s) => {
      if (s === "open") {
        setConnected(true);
        setStatus("idle");
        ws.send(
          JSON.stringify({ type: "language", value: language }),
        );
        ws.send(
          JSON.stringify({
            type: "spokenLanguage",
            value: spokenLanguage,
          }),
        );
        ws.send(
          JSON.stringify({ type: "activity", value: activity }),
        );
        ws.send(
          JSON.stringify({ type: "voice", value: settings.voice }),
        );
        ws.send(
          JSON.stringify({
            type: "ttsInstructions",
            value: settings.ttsInstructions,
          }),
        );
        ws.send(
          JSON.stringify({
            type: "ttsSpeed",
            value: String(settings.ttsSpeed),
          }),
        );
        ws.send(
          JSON.stringify({
            type: "memoryProvider",
            value: settings.memoryProvider,
          }),
        );
        ws.send(
          JSON.stringify({
            type: "chatModel",
            value: settings.chatModel,
          }),
        );
      }
      if (s === "closed") {
        setConnected(false);
        setStatus("offline");
      }
      if (s === "error") {
        setConnected(false);
        setStatus("error");
      }
    });
    wsRef.current = ws;
  };

  const disconnect = () => {
    startPendingRef.current = false;
    if (captureRef.current) {
      captureRef.current.stop();
      captureRef.current = null;
    }
    setListening(false);
    const ws = wsRef.current;
    if (ws) {
      try {
        ws.close();
      } catch { /* */ }
      wsRef.current = null;
    }
    setConnected(false);
    setStatus("offline");
  };

  // Keep disconnectRef pointing at the latest closure so stable callbacks
  // (handleMessage) can call it without going stale.
  disconnectRef.current = disconnect;

  // Open the WebSocket once on mount
  useEffect(() => {
    connect();
    return () => {
      disconnectRef.current();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleMessage]);

  // Sync language to server
  useEffect(() => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(
        JSON.stringify({
          type: "language",
          value: language,
        }),
      );
    }
  }, [language]);

  // Sync spoken (input) language to server
  useEffect(() => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(
        JSON.stringify({
          type: "spokenLanguage",
          value: spokenLanguage,
        }),
      );
    }
  }, [spokenLanguage]);

  // Sync activity to server
  useEffect(() => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(
        JSON.stringify({
          type: "activity",
          value: activity,
        }),
      );
    }
  }, [activity]);

  // Sync output + chat model settings to server
  useEffect(() => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify({ type: "voice", value: settings.voice }));
  }, [settings.voice]);
  useEffect(() => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(
      JSON.stringify({
        type: "ttsInstructions",
        value: settings.ttsInstructions,
      }),
    );
  }, [settings.ttsInstructions]);
  useEffect(() => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(
      JSON.stringify({
        type: "ttsSpeed",
        value: String(settings.ttsSpeed),
      }),
    );
  }, [settings.ttsSpeed]);
  useEffect(() => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(
      JSON.stringify({
        type: "memoryProvider",
        value: settings.memoryProvider,
      }),
    );
  }, [settings.memoryProvider]);
  useEffect(() => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(
      JSON.stringify({ type: "chatModel", value: settings.chatModel }),
    );
  }, [settings.chatModel]);

  // Auto-scroll transcripts
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [transcripts]);

  // Track fullscreen state so the icon flips when the user enters/exits
  // (including via Esc, or via the Safari/iPad system controls).
  useEffect(() => {
    const doc = document as Document & {
      webkitFullscreenElement?: Element | null;
    };
    const update = () =>
      setIsFullscreen(!!(doc.fullscreenElement ?? doc.webkitFullscreenElement));
    document.addEventListener("fullscreenchange", update);
    document.addEventListener("webkitfullscreenchange", update);
    update();
    return () => {
      document.removeEventListener("fullscreenchange", update);
      document.removeEventListener("webkitfullscreenchange", update);
    };
  }, []);

  const toggleFullscreen = () => {
    const elem = document.documentElement as HTMLElement & {
      webkitRequestFullscreen?: () => Promise<void> | void;
    };
    const doc = document as Document & {
      webkitExitFullscreen?: () => Promise<void> | void;
      webkitFullscreenElement?: Element | null;
    };
    const inFullscreen = !!(doc.fullscreenElement ??
      doc.webkitFullscreenElement);
    if (inFullscreen) {
      (doc.exitFullscreen ?? doc.webkitExitFullscreen)?.call(doc);
    } else {
      (elem.requestFullscreen ?? elem.webkitRequestFullscreen)?.call(elem);
    }
  };

  // Stop any in-flight capture when mic mode changes
  useEffect(() => {
    startPendingRef.current = false;
    if (captureRef.current) {
      captureRef.current.stop();
      captureRef.current = null;
      setListening(false);
      setStatus((s) => (s === "listening" ? "idle" : s));
      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: "flush" }));
      }
    }
  }, [micMode]);

  const startListening = async () => {
    if (
      !wsRef.current ||
      wsRef.current.readyState === WebSocket.CLOSED
    ) {
      connect();
      // Wait briefly for the socket to open before requesting the mic so
      // initial audio frames aren't dropped on the floor.
      await new Promise<void>((resolve) => {
        const start = Date.now();
        const tick = () => {
          const s = wsRef.current?.readyState;
          if (s === WebSocket.OPEN || Date.now() - start > 3000) {
            resolve();
          } else {
            window.setTimeout(tick, 50);
          }
        };
        tick();
      });
    }
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    if (captureRef.current) return;
    try {
      const capture = await startCapture((buf) => {
        if (ws.readyState === WebSocket.OPEN) ws.send(buf);
      });
      // If a release happened while permission/mic was resolving, abort.
      if (!startPendingRef.current) {
        capture.stop();
        return;
      }
      captureRef.current = capture;
      setListening(true);
      setStatus("listening");
    } catch (err) {
      console.error(err);
      setStatus("error");
      setMessage("Mic permission denied");
    }
  };

  const stopListening = () => {
    const wasActive = !!captureRef.current || startPendingRef.current;
    startPendingRef.current = false;
    if (captureRef.current) {
      captureRef.current.stop();
      captureRef.current = null;
    }
    setListening(false);
    setStatus("idle");
    // Tell the server to finalize the current STT utterance so the
    // "Thinking…" → audio response pipeline fires (especially important
    // for push-to-talk, where Google's silence detection wouldn't trigger).
    if (wasActive) {
      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: "flush" }));
      }
    }
  };

  const requestStart = () => {
    startPendingRef.current = true;
    startListening();
  };

  const toggleListening = () => {
    if (listening || captureRef.current) {
      stopListening();
    } else {
      requestStart();
    }
  };

  return (
    <main className="h-screen w-screen overflow-hidden p-6 flex flex-col gap-4">
      <header className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <span className="text-3xl">🎙️</span>
            <h1 className="text-2xl font-extrabold tracking-tight">
              Chit-Chat
            </h1>
          </div>
          <ActivitySelector
            value={activity}
            onChange={setActivity}
          />
          <SettingsPanel
            value={settings}
            activity={activity}
            onChange={setSettings}
          />
          <button
            onClick={toggleFullscreen}
            aria-label={isFullscreen
              ? "Exit fullscreen"
              : "Enter fullscreen"}
            title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
            className="h-9 w-9 grid place-items-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
          >
            {isFullscreen
              ? <Minimize className="h-4 w-4" strokeWidth={2.5} />
              : <Maximize className="h-4 w-4" strokeWidth={2.5} />}
          </button>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={connected ? disconnect : connect}
            aria-label={connected ? "Disconnect" : "Connect"}
            title={connected
              ? "Server can send messages. Click to disconnect."
              : "Not connected. Click to connect."}
            className={connected
              ? "h-9 px-3 inline-flex items-center gap-2 rounded-full bg-emerald-500/20 text-emerald-100 hover:bg-emerald-500/30 transition-colors text-xs font-semibold"
              : "h-9 px-3 inline-flex items-center gap-2 rounded-full bg-rose-500/20 text-rose-100 hover:bg-rose-500/30 transition-colors text-xs font-semibold"}
          >
            <span
              className={connected
                ? "h-2 w-2 rounded-full bg-emerald-400 animate-pulse"
                : "h-2 w-2 rounded-full bg-rose-400"}
            />
            {connected ? "Live" : "Offline"}
          </button>
          {self && (
            <span className="text-sm bg-white/10 rounded-full px-4 py-2">
              You are <strong>{self}</strong>
            </span>
          )}
          <StatusBanner status={status} message={message} />
        </div>
      </header>

      <section className="flex-1 grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-4 lg:gap-6 min-h-0">
        <Card className="flex flex-col p-4 lg:p-6 min-h-0">
          <div className="flex-1 flex flex-col items-center justify-center gap-3 md:gap-4 lg:gap-5 min-h-0">
            <MicButton
              listening={listening}
              mode={micMode}
              onToggle={toggleListening}
              onPress={requestStart}
              onRelease={stopListening}
            />
            <MicModeSelector value={micMode} onChange={setMicMode} />
            <p className="text-center text-white/70 max-w-sm text-sm lg:text-base">
              {micMode === "ptt"
                ? "Press and hold the mic to speak. Release to send."
                : "Tap the mic, then speak. Everyone connected hears your voice translated into their chosen language."}
            </p>
          </div>

          <div className="border-t border-white/10 pt-3 mt-3 flex flex-col items-center gap-2">
            <div className="flex items-center gap-3 flex-wrap justify-center">
              <p className="text-xs opacity-60 uppercase tracking-widest">
                Speak in
              </p>
              <SpokenLanguageSelector
                value={spokenLanguage}
                onChange={setSpokenLanguage}
              />
            </div>
            <p className="text-xs opacity-60 uppercase tracking-widest">
              Translate to
            </p>
            <LanguageSelector
              value={language}
              onChange={setLanguage}
            />
          </div>
        </Card>

        <Card className="flex flex-col p-6 min-h-0">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm uppercase tracking-widest opacity-60">
              Conversation
            </h2>
            {transcripts.length > 0 && (
              <button
                onClick={() => setTranscripts([])}
                aria-label="Clear conversation"
                className="text-xs uppercase tracking-wider px-3 py-1 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
              >
                Clear
              </button>
            )}
          </div>
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto flex flex-col gap-4 pr-2"
          >
            {transcripts.length === 0 ? (
              <div className="m-auto text-center text-white/50">
                <p className="text-6xl mb-3">👋</p>
                <p>No messages yet — say hi!</p>
              </div>
            ) : (
              transcripts.map((t) => (
                <TranscriptBubble key={t.id} t={t} />
              ))
            )}
          </div>
        </Card>
      </section>

    </main>
  );
}
